import { useMemo, useState } from 'react';

import {
  isBooleanParameter,
  isDateParameter,
  isDateRangeParameter,
  isDateTimeParameter,
  isDateTimeRangeParameter,
  isMultiSelectParameter,
  isNumericParameter,
  isSelectParameter,
  isTextParameter,
  ReportFormat,
  ReportParameter,
  ReportParameterValues,
  SelectOption,
} from '@drivingdoc/shared-types/reports';
import Backdrop from '@mui/material/Backdrop';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { DialogProps, useDialogs } from '@toolpad/core/useDialogs';
import { FormProvider, useForm } from 'react-hook-form';

import { generateReport } from 'api/hooks/private/reports/GenerateReport';

import SelectInput from 'components/FormInputs/SelectInput';

import { ReportDefinitionWithIcon } from 'types/reports';

import { getApiErrorMessage } from 'utils/getApiErrorMessage';
import { calculateDateFromOffset, resolveMultiSelectDefault } from 'utils/reportParameters';

import FeedbackDialog from '../FeedbackDialog';
import {
  BooleanParameterField,
  DateParameterField,
  DateRangeParameterField,
  DateTimeParameterField,
  MultiSelectParameterField,
  NumericParameterField,
  SelectParameterField,
  TextParameterField,
} from './components';

interface GenerateReportDialogPayload {
  report: ReportDefinitionWithIcon;
}

/**
 * Build default values from parameter configs
 */
function buildDefaultValues(params: ReportParameter[], defaultFormat: string): ReportParameterValues {
  const defaults: ReportParameterValues = {
    format: defaultFormat,
  };

  for (const param of params) {
    if (isDateParameter(param) && param.config?.defaultOffset) {
      defaults[param.name] = calculateDateFromOffset(param.config.defaultOffset).format('YYYY-MM-DD');
    }
    if (isDateTimeParameter(param) && param.config?.defaultOffset) {
      const date = calculateDateFromOffset(param.config.defaultOffset);
      const time = param.config.defaultTime ?? '00:00';
      defaults[param.name] = date.format('YYYY-MM-DD') + 'T' + time;
    }
    if (isDateRangeParameter(param) || isDateTimeRangeParameter(param)) {
      const config = param.config;
      if (config?.defaultStartOffset || config?.defaultEndOffset) {
        defaults[param.name] = {
          start: config.defaultStartOffset
            ? calculateDateFromOffset(config.defaultStartOffset).format('YYYY-MM-DD')
            : null,
          end: config.defaultEndOffset ? calculateDateFromOffset(config.defaultEndOffset).format('YYYY-MM-DD') : null,
        };
      }
    }
    if (isSelectParameter(param) && param.config.defaultValue) {
      defaults[param.name] = param.config.defaultValue;
    }
    if (isMultiSelectParameter(param) && param.config.defaultValue) {
      defaults[param.name] = resolveMultiSelectDefault(param.config.defaultValue, param.config.options);
    }
    if (isNumericParameter(param) && param.config?.defaultValue != null) {
      defaults[param.name] = param.config.defaultValue;
    }
    if (isBooleanParameter(param)) {
      defaults[param.name] = param.config?.defaultValue ?? false;
    }
    if (isTextParameter(param) && param.config?.defaultValue) {
      defaults[param.name] = param.config.defaultValue;
    }
  }

  return defaults;
}

function renderParameterField(param: ReportParameter) {
  if (isDateRangeParameter(param) || isDateTimeRangeParameter(param)) {
    return <DateRangeParameterField key={param.name} parameter={param} />;
  }
  if (isDateParameter(param)) {
    return <DateParameterField key={param.name} parameter={param} />;
  }
  if (isDateTimeParameter(param)) {
    return <DateTimeParameterField key={param.name} parameter={param} />;
  }
  if (isSelectParameter(param)) {
    return <SelectParameterField key={param.name} parameter={param} />;
  }
  if (isMultiSelectParameter(param)) {
    return <MultiSelectParameterField key={param.name} parameter={param} />;
  }
  if (isNumericParameter(param)) {
    return <NumericParameterField key={param.name} parameter={param} />;
  }
  if (isBooleanParameter(param)) {
    return <BooleanParameterField key={param.name} parameter={param} />;
  }
  if (isTextParameter(param)) {
    return <TextParameterField key={param.name} parameter={param} />;
  }
  return null;
}

function GenerateReportDialog({ payload, open, onClose }: DialogProps<GenerateReportDialogPayload>) {
  const { report } = payload;
  const dialogs = useDialogs();
  const [isGenerating, setIsGenerating] = useState(false);

  const defaultValues = useMemo(
    () => buildDefaultValues(report.parameters, report.defaultFormat ?? report.availableFormats[0] ?? 'csv'),
    [report.parameters, report.defaultFormat, report.availableFormats],
  );

  const form = useForm<ReportParameterValues>({
    mode: 'all',
    defaultValues,
  });

  const { handleSubmit } = form;

  const formatOptions = report.availableFormats.map((format) => ({
    value: format,
    label: format.toUpperCase(),
  }));

  const onSubmit = async (data: ReportParameterValues) => {
    try {
      setIsGenerating(true);

      // Extract format and remaining parameters
      const { format, ...parameters } = data;

      // Transform multi-select values from SelectOption[] to string[]
      const transformedParameters = Object.fromEntries(
        Object.entries(parameters).map(([key, value]) => {
          if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object' && 'value' in value[0]) {
            return [key, (value as SelectOption[]).map((opt) => opt.value)];
          }
          return [key, value];
        }),
      );

      await generateReport({
        reportId: report.id,
        format: format as ReportFormat,
        parameters: transformedParameters,
      });

      // Close the dialog
      // await onClose();

      // Show success message
      dialogs.open(FeedbackDialog, {
        icon: 'success',
        title: 'Report Generated',
        message: 'Your report has been downloaded.',
      });
    } catch (error) {
      dialogs.open(FeedbackDialog, {
        title: 'Error generating report',
        message: getApiErrorMessage(error),
      });
    } finally {
      setIsGenerating(false);
    }
  };

  // Sort parameters by order if specified
  const sortedParameters = [...report.parameters].sort((a, b) => (a.order ?? 999) - (b.order ?? 999));

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={() => onClose()}>
      <form noValidate onSubmit={handleSubmit(onSubmit)}>
        <FormProvider {...form}>
          <DialogTitle>{report.title}</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" mb={2}>
              {report.description}
            </Typography>
            <Grid container rowSpacing={2} columnSpacing={2}>
              {sortedParameters.map(renderParameterField)}
              <Grid size={12}>
                <SelectInput name="format" label="Output Format" options={formatOptions} />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => onClose()}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={isGenerating}>
              Generate Report
            </Button>
          </DialogActions>
        </FormProvider>
      </form>
      <Backdrop open={isGenerating} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </Dialog>
  );
}

export default GenerateReportDialog;
